import toast from 'react-hot-toast';
import { isNativeApp, openOutsideWebViewNative } from '@/shared/utils/nativeBridge';
import { toLegacyBrowserUrl } from './legacyDownloadLink';
import { UPDATE_APP_TO_DOWNLOAD_MESSAGE } from './messages';

const UPDATE_APP_TOAST_ID = 'update-app-to-download';

const isHttps = (url: string): boolean => /^https:\/\//i.test(url);

/**
 * Inside the app: the bridge when the binary has it; otherwise, in a binary without it,
 * a top-level navigation, which the shell intercepts and opens in the system browser
 * (no user gesture needed, so it is safe after an await). Our own signed links are
 * adapted so that the old shell takes them (see `toLegacyBrowserUrl`); any other
 * navigation it would load inside the WebView is never attempted: the user is asked
 * to update. `window.open` is not used here: the old shell answers it with a page
 * load in the WebView, and on iOS it returns `null` and may be blocked after an await.
 */
const openInNativeApp = (url: string): boolean => {
  if (isHttps(url) && openOutsideWebViewNative(url)) return true;
  const legacyUrl = toLegacyBrowserUrl(url);
  if (legacyUrl) {
    window.location.assign(legacyUrl);
    return true;
  }
  toast.error(UPDATE_APP_TO_DOWNLOAD_MESSAGE, { id: UPDATE_APP_TOAST_ID });
  return false;
};

const openInNewTab = (url: string): boolean => {
  const opened = window.open(url, '_blank');
  if (!opened) return false;
  try {
    opened.opener = null;
  } catch {
    // Non-fatal: the tab is open, it just keeps its opener.
  }
  return true;
};

/**
 * Opens a remote URL outside the page: the system browser inside the app, a new tab
 * everywhere else. Returns whether something was opened. In the app a `false` has
 * already been explained to the user with a toast (the binary is too old to open
 * that third-party file), so callers must not add an error of their own.
 */
export const openRemoteUrl = (url: string): boolean =>
  isNativeApp() ? openInNativeApp(url) : openInNewTab(url);
